import type { KnowledgeUnit } from '../types'

// ---------------------------------------------------------------------------
// routing.ts — routing heuristics + disambiguation rules
//
// Draft revision v0 (not yet analyst-greenlit per spec §18).
// INFERRED rules are marked; these are the agenda for the analyst tuning pass.
// ---------------------------------------------------------------------------

export const routing: KnowledgeUnit[] = [
  {
    id: 'routing',
    type: 'routing',
    keys: [
      'routing',
      'which page',
      'where to go',
      'navigation',
      'disambiguation',
      'page selection',
    ],
    version: '0.2-draft',
    title: 'Routing heuristics and disambiguation rules',
    summary:
      'Rules for routing user questions to the correct analytics page; covers the 6 analytics pages and 4 known ambiguities.',
    body: `# Routing heuristics (v0-draft — pending analyst greenlight per spec §18)

## Page roster (6 analytics pages)

| Page id | Route | Primary question answered |
|---|---|---|
| ReviewKpis | /review-status/review-kpis | How is the business performing? (actual sales, headline KPIs) |
| MarketingInvests | /review-status/marketing-invests | How much did we spend and where? (net spend breakdown) |
| CampaignPerformance | /review-performance/campaign-performance | How efficiently did campaigns convert spend? (ROI, CpO per campaign/period) |
| Contribution | /review-performance/contribution | What is driving sales? (media vs baseline decomposition) |
| Dynamics | /review-performance/dynamics | What changed over time and why? (metric trends + driver attribution) |
| MultiDeepDive | /review-performance/multi-deep-dive | How does one metric compare across many dimension combinations? (bar drill) |

## Primary routing signals

1. SPEND questions ("how much did we spend?", "what was the budget?", "media investment?") → MarketingInvests (nsp lead KPI).
2. HEADLINE RESULTS / TOTAL SALES ("how are sales?", "overall performance?", "hitting targets?") → ReviewKpis (actual_sales lead KPI).
3. EFFICIENCY / ROI / PROFITABILITY / COST ("was it efficient?", "what was the ROI?", "cost per order?") → CampaignPerformance (sales_roi / sales_cpo).
4. DECOMPOSITION / ATTRIBUTION ("what drove sales?", "what share came from media?", "baseline contribution?") → Contribution (sales__media__units).
5. TEMPORAL / TREND / CAUSATION ("what changed?", "why did it drop?", "trend over time?", "what drove the shift?") → Dynamics.
6. MULTI-DIMENSION DRILL / COMPARISON ACROSS MANY FACETS ("compare across regions AND campaigns AND periods?", "bar breakdown across all dimensions?") → MultiDeepDive.

## Non-analytics routes (routing-only for MVP — do NOT navigate there)
Home (pinned dashboard), Collections (saved analyses), Account, Profile are out of scope. If the user asks about these, explain and offer to route to an analytics page instead.

---

## Disambiguation rules (4 known ambiguities — INFERRED, pending analyst tuning)

### 1. CampaignPerformance vs Dynamics
Question frame: "compare Q3 vs Q4 efficiency" or "how did ROI change between periods?"

INFERRED routing rule:
- Prefer CampaignPerformance when: the question is campaign-granular (comparing specific named campaigns or using group-by), or when multi-timeframe period bars are the right visualization.
- Prefer Dynamics when: the question has a causal/explanatory frame ("WHY did ROI change?", "what DROVE the shift?") or when the user wants a temporal narrative with driver attribution panels over multiple periods.
- Tiebreaker: if the question names a specific campaign → CampaignPerformance; if the question asks about what caused a change without naming a campaign → Dynamics.
- When uncertain: ask the user whether they want to compare campaign-level numbers (CP) or understand the temporal causal story (Dynamics).

### 2. Contribution vs MultiDeepDive
Question frame: "sales broken down by channel and region"

INFERRED routing rule:
- Prefer Contribution when: the question is about DECOMPOSITION (shares, stacked attribution — "what percentage came from media?", "how much is baseline?") and uses the model_success_kpi decomposition.
- Prefer MultiDeepDive when: the question is about COMPARISON across dimension combinations (bars across multiple facets simultaneously — "compare RoI by channel AND region AND time period").
- Key heuristic: Contribution = "what's driving sales?" (stacked, one metric); MultiDeepDive = "how does it compare across many cuts?" (bars, multiple dimensions).
- When uncertain: ask whether the user wants a decomposition view (how is sales built up from drivers?) or a comparison view (how does the metric rank across dimensions?).

### 3. "Sales by channel" → which page?
Question frame: "show me sales by channel" or "how do the channels compare?"

INFERRED routing rule:
- If framed as ATTRIBUTION / CONTRIBUTION ("what share of sales did each channel drive?") → Contribution.
- If framed as COMPARISON ACROSS TIME ("how did sales by channel change over the year?") → Dynamics.
- If framed as RANKING / BAR COMPARISON across multiple dimensions → MultiDeepDive.
- "Sales by channel" without further context → Contribution is the safer default (it directly shows channel-level decomposition of model-attributed sales).
- INFERRED — confirm with analyst which page should be the canonical home for "channel performance."

### 4. ReviewKpis vs MarketingInvests — spend overlap
Question frame: "how did spend impact results?" or "show me spend alongside sales"

INFERRED routing rule:
- If the primary question is SPEND ("how much did we spend? where?") → MarketingInvests.
- If the primary question is BUSINESS RESULTS / ACTUALS ("how are sales?", "overall KPI health?") → ReviewKpis.
- Some spend metrics may be visible on ReviewKpis alongside actuals. INFERRED: treat MarketingInvests as the AUTHORITATIVE page for spend analysis; ReviewKpis for the headline actuals view.
- When a question asks about BOTH spend and results together → suggest starting on ReviewKpis for the headline, then navigating to MarketingInvests for the spend breakdown, then CampaignPerformance for ROI.
- INFERRED — the exact KPI roster on each status page is confirmed by the analyst via get_controls; do not assume spend metrics are absent from ReviewKpis.

---

## General guidance

- Always confirm the current page before routing — if the user is already on the right page, steer in-place rather than propose navigation.
- Offer one-click navigation proposals (propose_navigation tool) rather than telling the user to navigate manually.
- If a question spans multiple pages, answer the most specific part first, then offer to go deeper on the other page.
- If the question is about the model, drivers, or methodology rather than a specific page's data → use search_knowledge to retrieve the relevant concept or KPI unit and answer from grounding.
- When uncertain about routing, ask a single clarifying question rather than guessing.

Data access is page-independent: query_kpi reads any page's data via its page parameter. Route the USER (propose_navigation) when seeing a page helps them — never as a precondition for answering a data question.`,
    seeAlso: [
      'page:ReviewKpis',
      'page:MarketingInvests',
      'page:CampaignPerformance',
      'page:Contribution',
      'page:Dynamics',
      'page:MultiDeepDive',
    ],
  },
]
